import type { ApiSchemas } from '@rental/contracts/client'
import { format, formatEuro } from '@rental/i18n'
import { Field, Notice } from '@rental/ui'
import { Section } from '../components/Section'
import { FIELD, FIELD_ROW, StaffInput } from '../components/ui'
import { useLocale } from '../i18n'
import { type Lookups, refusalText } from '../refusals'
import {
  OLD_SITE_REFERENCE_MAX,
  type OldSiteFields,
  type OldSiteGap,
  type OldSiteReading,
} from './new-booking-old-site'

type Refusal = ApiSchemas['Refusal']

/** Each gap in the form's words: what the button waits for. */
export function useOldSiteGapWords(): Record<OldSiteGap, string> {
  const { messages: m } = useLocale()
  const words = m.newBooking.oldSite
  return {
    reference: words.needReference,
    agreedTotal: words.needAgreedTotal,
    paid: words.needPaid,
    stripePaymentIntentId: words.needStripeId,
    paidForStripe: words.needPaidForStripe,
  }
}

/**
 * S9 (ADM-4): what the old site agreed and took, typed from its confirmation.
 * The price the booking costs is the one agreed there; what was paid there is
 * recorded apart, checked with Stripe when its id is typed.
 */
export function OldSiteSection({
  fields,
  onChange,
  reading,
}: {
  fields: OldSiteFields
  onChange: (fields: OldSiteFields) => void
  reading: OldSiteReading
}) {
  const { messages: m } = useLocale()
  const words = m.newBooking.oldSite
  const gapWords = useOldSiteGapWords()
  // A field says what is wrong with it once something is typed there, never while it is empty.
  const typedGap = (gap: OldSiteGap, typed: string) =>
    typed.trim() !== '' && reading.gaps.includes(gap) ? gapWords[gap] : null
  return (
    <Section title={words.section}>
      <div className={FIELD_ROW}>
        <Field className={FIELD} label={words.reference} hint={words.referenceHint} required>
          {(control) => (
            <StaffInput
              {...control}
              autoComplete="off"
              maxLength={OLD_SITE_REFERENCE_MAX}
              value={fields.reference}
              onChange={(event) => onChange({ ...fields, reference: event.target.value })}
            />
          )}
        </Field>
        <Field
          className={FIELD}
          label={words.agreedTotal}
          hint={words.agreedTotalHint}
          required
          error={typedGap('agreedTotal', fields.agreedTotal)}
        >
          {(control) => (
            <StaffInput
              {...control}
              inputMode="decimal"
              value={fields.agreedTotal}
              onChange={(event) => onChange({ ...fields, agreedTotal: event.target.value })}
            />
          )}
        </Field>
      </div>
      <div className={FIELD_ROW}>
        <Field
          className={FIELD}
          label={words.paid}
          hint={words.paidHint}
          optionalLabel={m.newBooking.optional}
          error={typedGap('paid', fields.paid)}
        >
          {(control) => (
            <StaffInput
              {...control}
              inputMode="decimal"
              value={fields.paid}
              onChange={(event) => onChange({ ...fields, paid: event.target.value })}
            />
          )}
        </Field>
        <Field
          className={FIELD}
          label={words.stripeId}
          hint={words.stripeIdHint}
          optionalLabel={m.newBooking.optional}
          error={
            typedGap('stripePaymentIntentId', fields.stripePaymentIntentId) ??
            typedGap('paidForStripe', fields.stripePaymentIntentId)
          }
        >
          {(control) => (
            <StaffInput
              {...control}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              value={fields.stripePaymentIntentId}
              onChange={(event) =>
                onChange({ ...fields, stripePaymentIntentId: event.target.value })
              }
            />
          )}
        </Field>
      </div>
      <p className="text-sm text-text-muted">{words.noEmail}</p>
    </Section>
  )
}

/** The old site's rules this rental breaks, waived: said, never a refusal. */
export function WaivedRules({
  waived,
  lookups,
  listId,
  title,
}: {
  waived: readonly Refusal[]
  lookups: Lookups
  listId?: string
  /** S2: an ordinary counter booking names what the website alone would refuse. */
  title?: string
}) {
  const { locale, messages: m } = useLocale()
  if (waived.length === 0) return null
  return (
    <Notice tone="warning" title={title ?? m.newBooking.oldSite.waivedTitle}>
      <ul id={listId} className="flex list-disc flex-col gap-1 ps-6">
        {waived.map((refusal) => (
          <li key={refusal.code}>{refusalText(refusal, locale, m, lookups)}</li>
        ))}
      </ul>
    </Notice>
  )
}

/**
 * D11: the booking said back before it is recorded: the reference, the price
 * agreed, what was paid there and how, what is left, and the rules waived. An
 * amount typed without Stripe's id is never corrected afterwards, so it says so.
 */
export function OldSiteSummary({
  request,
  balanceCents,
  listCents,
  waived,
  lookups,
}: {
  request: {
    reference: string
    agreedTotalCents: number
    paid?: { amountCents: number; stripePaymentIntentId?: string }
  }
  balanceCents: number
  listCents: number | null
  waived: readonly Refusal[]
  lookups: Lookups
}) {
  const { locale, messages: m } = useLocale()
  const words = m.newBooking.oldSite
  const paid = request.paid
  return (
    <div className="flex flex-col gap-3">
      <dl className="flex flex-col gap-2">
        <div className="flex flex-col">
          <dt className="text-sm text-text-muted">{words.reference}</dt>
          <dd className="wrap-anywhere">{request.reference}</dd>
        </div>
        <div className="flex flex-col">
          <dt className="text-sm text-text-muted">{words.agreed}</dt>
          <dd className="figures">{formatEuro(request.agreedTotalCents, locale)}</dd>
        </div>
        {listCents !== null && listCents !== request.agreedTotalCents ? (
          <div className="flex flex-col">
            <dt className="text-sm text-text-muted">{words.listPrice}</dt>
            <dd className="figures">{formatEuro(listCents, locale)}</dd>
          </div>
        ) : null}
        <div className="flex flex-col">
          <dt className="text-sm text-text-muted">{words.paidThere}</dt>
          <dd className="figures wrap-anywhere">
            {!paid
              ? words.nothingPaid
              : paid.stripePaymentIntentId
                ? format(words.paidWith, {
                    amount: formatEuro(paid.amountCents, locale),
                    id: paid.stripePaymentIntentId,
                  })
                : format(words.paidWithout, { amount: formatEuro(paid.amountCents, locale) })}
          </dd>
        </div>
        <div className="flex flex-col">
          <dt className="text-sm text-text-muted">{balanceCents < 0 ? words.credit : words.due}</dt>
          <dd className="figures font-bold">{formatEuro(Math.abs(balanceCents), locale)}</dd>
        </div>
      </dl>
      {balanceCents < 0 ? (
        <Notice tone="warning">
          <p>{words.overpaid}</p>
        </Notice>
      ) : null}
      <WaivedRules waived={waived} lookups={lookups} />
      {paid && !paid.stripePaymentIntentId ? (
        <p className="font-medium">{words.noCorrection}</p>
      ) : null}
    </div>
  )
}
